import { useState } from "react";
import {
  Download,
  SlidersHorizontal,
  Copy,
  AlertTriangle,
} from "lucide-react";
import { type CurrentUser } from "../../lib/api";

const DEMO_QUEUE_REPORTS = [
  {
    id: "q-1",
    tenant_name: "Interlink Consultant",
    tenant_phone: "919323818985",
    user_name: "DELUXE",
    user_phone: "918454818800",
    instance_name: "Default instance",
    instance_key: "clvzd34rk170wsov",
    connection_detail: "NA",
    connection_state: "disconnected",
    queue_date: "2026-08-29",
    queue_count: 92,
  },
  {
    id: "q-2",
    tenant_name: "Interlink Consultant",
    tenant_phone: "919323818985",
    user_name: "DELUXE",
    user_phone: "918454818800",
    instance_name: "Default instance",
    instance_key: "clvzd34rk170wsov",
    connection_detail: "NA",
    connection_state: "disconnected",
    queue_date: "2026-08-28",
    queue_count: 48,
  },
  {
    id: "q-3",
    tenant_name: "Interlink Consultant",
    tenant_phone: "919323818985",
    user_name: "fid",
    user_phone: "918880003786",
    instance_name: "Default instance",
    instance_key: "cm8781ior17ayxxu",
    connection_detail: "La Marca 918880003786",
    connection_state: "connected",
    queue_date: "2026-08-28",
    queue_count: 12,
  },
  {
    id: "q-4",
    tenant_name: "Interlink Consultant",
    tenant_phone: "919323818985",
    user_name: "aristo",
    user_phone: "919326684839",
    instance_name: "Default instance",
    instance_key: "cmfdiel621mng13s",
    connection_detail: "Aristo tailor 919326684839",
    connection_state: "connected",
    queue_date: "2026-08-29",
    queue_count: 6,
  },
  {
    id: "q-5",
    tenant_name: "Interlink Consultant",
    tenant_phone: "919323818985",
    user_name: "nmt",
    user_phone: "919769580202",
    instance_name: "Default instance",
    instance_key: "clvxji691t105m51",
    connection_detail: "NA",
    connection_state: "disconnected",
    queue_date: "2026-08-26",
    queue_count: 5,
  },
  {
    id: "q-6",
    tenant_name: "Interlink Consultant",
    tenant_phone: "919323818985",
    user_name: "capler",
    user_phone: "917219869550",
    instance_name: "Default instance",
    instance_key: "cmfs9yywsksxrpck",
    connection_detail: "Capler Fashion",
    connection_state: "connected",
    queue_date: "2026-08-29",
    queue_count: 2,
  },
];

export function QRMessageQueueView({}: { token: string; user: CurrentUser }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [connFilter, setConnFilter] = useState("all");

  const filteredQueue = DEMO_QUEUE_REPORTS.filter((item) => {
    const matchesSearch =
      item.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.user_phone.includes(searchQuery) ||
      item.instance_key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesConn = connFilter === "all" || item.connection_state === connFilter;
    return matchesSearch && matchesConn;
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header (Matches 05_message_queue.png) */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Message Queue Report</h1>
        <p className="text-xs text-slate-500 mt-1">
          Monitor message queue status, connection states, and queue counts across all instances
        </p>
      </div>

      {/* Filter Bar (Matches 05_message_queue.png Filter Toolbar) */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search message queues..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Connection Filter */}
          <select
            value={connFilter}
            onChange={(e) => setConnFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">Connection: All Connections</option>
            <option value="connected">Connected</option>
            <option value="disconnected">Disconnected</option>
          </select>
        </div>

        {/* Action Buttons: Export Report & View */}
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs">
            <Download className="w-3.5 h-3.5 text-slate-500" />
            <span>Export Report</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Message Queue Table (Exact layout from 05_message_queue.png) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>User</th>
                <th>Instance</th>
                <th>Connection Details</th>
                <th>Connection State</th>
                <th>Queue Date</th>
                <th>Queue Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQueue.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                  {/* Organization */}
                  <td>
                    <div className="font-semibold text-slate-800 text-xs">{item.tenant_name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{item.tenant_phone}</div>
                  </td>

                  {/* User */}
                  <td>
                    <div className="font-bold text-slate-900 text-xs">{item.user_name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{item.user_phone}</div>
                  </td>

                  {/* Instance */}
                  <td>
                    <div className="font-semibold text-slate-900 text-xs">{item.instance_name}</div>
                    <div className="flex items-center gap-1 text-[11px] font-mono text-slate-500">
                      <button onClick={() => copyText(item.instance_key)} className="hover:text-slate-900">
                        <Copy className="w-3 h-3 text-slate-400" />
                      </button>
                      <span>{item.instance_key.slice(0, 14)}...</span>
                    </div>
                  </td>

                  {/* Connection Details */}
                  <td className="text-xs text-slate-700">{item.connection_detail}</td>

                  {/* Connection State */}
                  <td>
                    <span
                      className={`status-pill ${
                        item.connection_state === "connected"
                          ? "status-pill--connected"
                          : "status-pill--disconnected"
                      }`}
                    >
                      {item.connection_state === "connected" ? "Connected" : "Disconnected"}
                    </span>
                  </td>

                  {/* Queue Date */}
                  <td className="font-mono text-xs text-slate-600">{item.queue_date}</td>

                  {/* Queue Count */}
                  <td className="font-mono text-xs font-bold text-slate-900">{item.queue_count}</td>

                  {/* Fail Queue Button (Matches exact eNotify red button) */}
                  <td>
                    <button
                      onClick={() => alert(`Failed queue for ${item.user_name}`)}
                      className="bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-semibold py-1.5 px-3 rounded-md flex items-center gap-1.5 transition-colors shadow-2xs"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>Fail Queue</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
